import type {
  Account,
  AppNotification,
  Card,
  CashflowPoint,
  Corridor,
  Id,
  Rate,
  Recipient,
  SpendingSlice,
  Transaction,
  User,
} from '../types'
import { fromMajor } from '@/lib/money'
import { BRAND } from '@/brand/brand'

const id = <T extends string>(value: string): Id<T> => value as Id<T>

export const user: User = {
  id: id<'user'>('usr_alex'),
  firstName: 'Alex',
  lastName: 'Turner',
  email: `alex@${BRAND.domain}`,
  tier: 2,
  verified: true,
  plan: 'free',
}

export const accounts: readonly Account[] = [
  {
    id: id<'account'>('acc_usd'),
    currency: 'USD',
    label: 'US Dollar',
    balance: fromMajor(18240.5, 'USD'),
    primary: true,
    rail: 'Routing · ACH',
    maskedNumber: '••4568',
  },
  {
    id: id<'account'>('acc_eur'),
    currency: 'EUR',
    label: 'Euro',
    balance: fromMajor(12904.18, 'EUR'),
    primary: false,
    rail: 'IBAN · SEPA',
    maskedNumber: '••2231',
  },
  {
    id: id<'account'>('acc_mxn'),
    currency: 'MXN',
    label: 'Mexican Peso',
    balance: fromMajor(35900.5, 'MXN'),
    primary: false,
    rail: 'CLABE · SPEI',
    maskedNumber: '••8458',
  },
  {
    id: id<'account'>('acc_usdc'),
    currency: 'USDC',
    label: 'USDC',
    balance: fromMajor(4120.0, 'USDC'),
    primary: false,
    rail: 'Base · Solana',
    maskedNumber: '0x··9f2a',
  },
  {
    id: id<'account'>('acc_brl'),
    currency: 'BRL',
    label: 'Brazilian Real',
    balance: fromMajor(0, 'BRL'),
    primary: false,
    rail: 'PIX key · TED',
    maskedNumber: '••7742',
    status: 'awaiting-deposit',
  },
]

export const cards: readonly Card[] = [
  {
    id: id<'card'>('crd_physical'),
    kind: 'physical',
    label: 'Everyday',
    network: 'mastercard',
    last4: '4568',
    status: 'active',
    holder: 'ALEX TURNER',
    expiry: '09/29',
    spentThisMonth: fromMajor(1204.6, 'USD'),
    monthlyLimit: fromMajor(4000, 'USD'),
    wallets: ['apple', 'google'],
  },
  {
    id: id<'card'>('crd_virtual'),
    kind: 'virtual',
    label: 'Online',
    network: 'visa',
    last4: '1182',
    status: 'active',
    holder: 'ALEX TURNER',
    expiry: '03/28',
    spentThisMonth: fromMajor(318.42, 'USD'),
    monthlyLimit: fromMajor(1000, 'USD'),
    wallets: [],
  },
  {
    id: id<'card'>('crd_ordered'),
    kind: 'physical',
    label: 'New physical',
    network: 'mastercard',
    last4: '9013',
    status: 'ordered',
    holder: 'ALEX TURNER',
    expiry: '09/31',
    spentThisMonth: fromMajor(0, 'USD'),
    monthlyLimit: fromMajor(4000, 'USD'),
    wallets: [],
    delivery: {
      stage: 'posted',
      eta: 'Tuesday 29 September',
      carrier: 'TrackWise',
      tracking: 'TW-4471-90A2',
    },
  },
  {
    id: id<'card'>('crd_agent'),
    kind: 'agent',
    label: 'Grocery run',
    network: 'visa',
    last4: '7730',
    status: 'active',
    holder: 'PASS AGENT',
    expiry: '11/26',
    spentThisMonth: fromMajor(86.4, 'USD'),
    monthlyLimit: fromMajor(300, 'USD'),
    wallets: [],
    agent: {
      task: 'Weekly groceries and pharmacy',
      perTransaction: fromMajor(60, 'USD'),
      merchants: ['Soriana', 'Farmacias del Ahorro', 'Chedraui'],
      expires: '2026-10-31',
      transactionsLeft: 6,
    },
  },
]

interface TransactionSeed {
  readonly ref: string
  readonly cat: string
  readonly ic: string
  readonly amt: number
  readonly at: string
  readonly cb?: number | undefined
}

const transactionSeeds: readonly TransactionSeed[] = [
  { ref: 'Mercadona', cat: 'Groceries', ic: 'storefront', amt: -64.2, at: '2026-09-20T11:24:00Z', cb: 0.32 },
  { ref: 'Payroll · Northwind', cat: 'Income', ic: 'payments', amt: 3120, at: '2026-09-19T08:00:00Z' },
  { ref: 'Uber', cat: 'Transport', ic: 'local_taxi', amt: -12.4, at: '2026-09-19T21:10:00Z', cb: 0.06 },
  { ref: 'To María Fernanda', cat: 'Sent to Mexico', ic: 'send', amt: -500, at: '2026-09-18T16:02:00Z' },
  { ref: 'Farmacia Guadalajara', cat: 'Pharmacy', ic: 'local_pharmacy', amt: -28.9, at: '2026-09-17T13:45:00Z', cb: 0.14 },
  { ref: 'Netflix', cat: 'Subscription', ic: 'subscriptions', amt: -15.99, at: '2026-09-15T06:00:00Z', cb: 0.08 },
  { ref: 'Casa Polanco', cat: 'Travel', ic: 'hotel', amt: -558, at: '2026-09-14T09:12:00Z', cb: 16.74 },
  { ref: 'Pemex', cat: 'Fuel', ic: 'local_gas_station', amt: -46, at: '2026-09-13T18:30:00Z', cb: 0.23 },
]

export const transactions: readonly Transaction[] = transactionSeeds.map((row, index) => ({
  id: id<'transaction'>(`txn_${index}`),
  counterparty: row.ref,
  category: row.cat,
  icon: row.ic,
  amount: fromMajor(row.amt, 'USD'),
  direction: row.amt >= 0 ? ('in' as const) : ('out' as const),
  status: 'settled' as const,
  occurredAt: row.at,
  ...(row.cb === undefined ? {} : { cashback: fromMajor(row.cb, 'USD') }),
}))

export const spending: readonly SpendingSlice[] = [
  { category: 'Sent abroad', icon: 'send', amount: fromMajor(1500, 'USD'), share: 0.52 },
  { category: 'Groceries', icon: 'storefront', amount: fromMajor(486.3, 'USD'), share: 0.17 },
  { category: 'Travel', icon: 'flight', amount: fromMajor(558, 'USD'), share: 0.19 },
  { category: 'Everything else', icon: 'more_horiz', amount: fromMajor(348.2, 'USD'), share: 0.12 },
]

const cashflowSeeds: ReadonlyArray<readonly [string, number, number]> = [
  ['Apr', 3120, 2480],
  ['May', 3120, 2960],
  ['Jun', 3420, 2610],
  ['Jul', 3120, 3040],
  ['Aug', 3520, 2890],
  ['Sep', 3120, 2892],
]

export const cashflow: readonly CashflowPoint[] = cashflowSeeds.map(([label, inbound, outbound]) => ({
  label,
  inbound: fromMajor(inbound, 'USD'),
  outbound: fromMajor(outbound, 'USD'),
}))

export const corridors: readonly Corridor[] = [
  { id: id<'corridor'>('cor_mx'), country: 'Mexico', currency: 'MXN', rail: 'SPEI', midRate: 17.42, spread: 0.009, arrival: 'In minutes' },
  { id: id<'corridor'>('cor_co'), country: 'Colombia', currency: 'COP', rail: 'Local ACH', midRate: 3920.0, spread: 0.011, arrival: 'Same day' },
  { id: id<'corridor'>('cor_br'), country: 'Brazil', currency: 'BRL', rail: 'PIX', midRate: 5.34, spread: 0.01, arrival: 'In minutes' },
  {
    id: id<'corridor'>('cor_bo'),
    country: 'Bolivia',
    currency: 'BOB',
    rail: 'USDC or bank',
    midRate: 6.91,
    spread: 0.014,
    arrival: 'Same day',
    pegged: true,
    note: 'The boliviano is pegged, and the street rate is not the official one. We deliver dollars — what they are worth in bolivianos is decided where they are cashed, not by us.',
  },
  { id: id<'corridor'>('cor_us'), country: 'United States', currency: 'USD', rail: 'ACH', midRate: 1, spread: 0, arrival: 'Same day' },
]

export const recipients: readonly Recipient[] = [
  { id: id<'recipient'>('rcp_maria'), name: 'María Fernanda Ruiz', corridorId: id<'corridor'>('cor_mx'), maskedAccount: 'CLABE ••4412', lastSent: '2026-09-18T16:02:00Z' },
  { id: id<'recipient'>('rcp_jorge'), name: 'Jorge Andrés Peña', corridorId: id<'corridor'>('cor_co'), maskedAccount: 'Bancolombia ••8871' },
  { id: id<'recipient'>('rcp_lucia'), name: 'Lucía Salazar', corridorId: id<'corridor'>('cor_bo'), maskedAccount: 'BNB ••2209', lastSent: '2026-08-02T10:30:00Z' },
]

export const rates: readonly Rate[] = [
  { pair: 'USD/MXN', base: 'USD', quote: 'MXN', value: 17.42, change: -0.0031, decimals: 2 },
  { pair: 'USD/COP', base: 'USD', quote: 'COP', value: 3920.4, change: 0.0058, decimals: 1 },
  { pair: 'USD/BRL', base: 'USD', quote: 'BRL', value: 5.34, change: 0.0012, decimals: 2 },
  { pair: 'USD/BOB', base: 'USD', quote: 'BOB', value: 6.91, change: 0, decimals: 2, pegged: true },
  { pair: 'EUR/USD', base: 'EUR', quote: 'USD', value: 1.081, change: 0.0024, decimals: 4 },
  { pair: 'GBP/USD', base: 'GBP', quote: 'USD', value: 1.2714, change: -0.0016, decimals: 4 },
  { pair: 'BTC/USD', base: 'BTC', quote: 'USD', value: 68240, change: 0.0212, decimals: 0 },
  { pair: 'ETH/USD', base: 'ETH', quote: 'USD', value: 3723.02, change: -0.0087, decimals: 2 },
  { pair: 'USDC/USD', base: 'USDC', quote: 'USD', value: 1.0, change: 0.0001, decimals: 4 },
]

interface NotificationSeed {
  readonly kind: AppNotification['kind']
  readonly icon: string
  readonly title: string
  readonly body: string
  readonly at: string
  readonly read: boolean
  readonly to?: string
}

const notificationSeeds: readonly NotificationSeed[] = [
  { kind: 'money-in', icon: 'south_west', title: 'Payroll landed', body: 'Northwind paid you $3,120.00 into your USD account.', at: '2026-09-22T08:02:00Z', read: false, to: '/transactions' },
  { kind: 'money-out', icon: 'north_east', title: 'Your transfer arrived', body: 'María Fernanda received MX$8,631.61 in Mexico.', at: '2026-09-21T16:04:00Z', read: false, to: '/transfers' },
  { kind: 'card', icon: 'redeem', title: 'Cashback is on the way', body: '$1.56 from September lands on 1 October.', at: '2026-09-21T09:00:00Z', read: false, to: '/cashback' },
  { kind: 'product', icon: 'trending_up', title: 'The Vault rate moved up', body: 'SOFR rose, so you now earn 2.05% instead of 1.98%.', at: '2026-09-20T11:30:00Z', read: true, to: '/invest' },
  { kind: 'card', icon: 'credit_card', title: 'Your physical card shipped', body: 'It should reach Queens in about five days.', at: '2026-09-19T14:12:00Z', read: true, to: '/cards' },
  { kind: 'security', icon: 'phonelink_lock', title: 'New device signed in', body: 'Chrome on macOS, from New York. It was you if you just logged in.', at: '2026-09-19T08:44:00Z', read: true, to: '/profile' },
  { kind: 'money-out', icon: 'flight', title: 'Fare alert · JFK → MEX', body: 'Dropped to $184, the lowest in six weeks.', at: '2026-09-18T07:20:00Z', read: true, to: '/travel/flights' },
]

export const notifications: readonly AppNotification[] = notificationSeeds.map((row, index) => ({
  id: id<'notification'>(`ntf_${index}`),
  kind: row.kind,
  icon: row.icon,
  title: row.title,
  body: row.body,
  at: row.at,
  read: row.read,
  ...(row.to === undefined ? {} : { to: row.to }),
}))
